import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { downloadInvoicePdf } from '../api';
import type { InvoiceSummary } from '../types';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { formatCatalogPrice } from '@/features/products/format-price';
import { ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * Read-only — an invoice is generated automatically at checkout and never
 * edited (append-only backend). The one action is "Télécharger", which
 * fetches the PDF through the authenticated endpoint (see `api.ts`).
 * A "Non fiscale" badge is shown whenever the row's own
 * `legalFieldsEnabled` snapshot is false (OQ-04) — the merchant is never
 * left to assume a document is tax-compliant when it is not.
 *
 * Sprint UX/UI Merchant Dashboard V2 — that badge now renders `tone="neutral"`
 * rather than `"warn"`: nothing here is wrong or needs the merchant's
 * action (OQ-04 is a platform-side, not merchant-side, pending decision),
 * so a warning colour was misleading. The download action is now a real
 * `Button`, not a bare text link — the one thing this screen lets the
 * merchant DO deserves to look like an action.
 */
export function InvoiceTable({ invoices }: { invoices: readonly InvoiceSummary[] }): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { accessToken, activeShopId } = useSession();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function onDownload(invoice: InvoiceSummary): Promise<void> {
    setDownloadingId(invoice.id);
    try {
      await downloadInvoicePdf(invoice.id, invoice.invoiceNumber, accessToken, activeShopId);
    } catch (error) {
      toast.error(
        error instanceof ApiError
          ? t(resolveErrorMessageKey(error))
          : t('merchant.invoices.downloadFailed'),
      );
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Table caption={t('merchant.nav.invoices')}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>{t('merchant.invoices.table.colInvoice')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.orders.table.colOrder')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.orders.table.colDate')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.invoices.table.colAmount')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
          <TableHeaderCell> </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {invoices.map((invoice) => (
          <TableRow key={invoice.id}>
            <TableCell>
              <span className="font-mono font-semibold text-ink">
                {t('merchant.invoices.invoiceNumberPrefix')} {invoice.invoiceNumber}
              </span>
            </TableCell>
            <TableCell>
              <span className="num text-sm text-ink">
                {invoice.orderNumber !== null ? `#${invoice.orderNumber}` : '—'}
              </span>
            </TableCell>
            <TableCell>
              <span className="text-sm text-ink">
                {new Date(invoice.issueDate).toLocaleDateString('fr-TN')}
              </span>
            </TableCell>
            <TableCell>
              <span className="num text-sm text-ink">{formatCatalogPrice(invoice.grandTotal)}</span>
            </TableCell>
            <TableCell>
              {invoice.legalFieldsEnabled ? (
                <Badge tone="good">{t('merchant.invoices.fiscal')}</Badge>
              ) : (
                <Badge tone="neutral">{t('merchant.invoices.nonFiscal')}</Badge>
              )}
            </TableCell>
            <TableCell align="end">
              <Button
                size="sm"
                variant="secondary"
                loading={downloadingId === invoice.id}
                onClick={() => {
                  void onDownload(invoice);
                }}
              >
                {t('merchant.invoices.downloadPdf')}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
